import { suppressClientMonitoringEvent } from './clientErrorNoise';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface ErrorReport {
  message: string;
  stack?: string;
  url?: string;
  component?: string;
  userId?: string;
  userEmail?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
  severity?: 'fatal' | 'error' | 'warning' | 'info';
}

const reportedErrors = new Set<string>();
const DEDUP_WINDOW = 30_000;

const silentPatterns = [
  'ResizeObserver loop',
  'Non-Error promise rejection captured',
  'Script error.',
  'NS_ERROR_',
  'ChunkLoadError',
  'Loading chunk',
  // Extension-mutated DOM vs React reconcile (see clientErrorNoise.ts)
  "Failed to execute 'removeChild' on 'Node'",
  "Failed to execute 'insertBefore' on 'Node'",
  'The node to be removed is not a child of this node',
  // Android In-App Browser WebView bridge teardown
  'Java object is gone',
  'Error invoking postMessage',
];

function getFingerprint(message: string, url?: string): string {
  return `${message}::${url || ''}`;
}

function isDuplicate(fingerprint: string): boolean {
  if (reportedErrors.has(fingerprint)) return true;
  reportedErrors.add(fingerprint);
  setTimeout(() => reportedErrors.delete(fingerprint), DEDUP_WINDOW);
  return false;
}

function getUserFromToken(): { userId?: string; userEmail?: string } {
  try {
    const token = localStorage.getItem('token');
    if (!token) return {};
    const payload = JSON.parse(atob(token.split('.')[1]));
    return { userId: payload.id, userEmail: payload.email };
  } catch {
    return {};
  }
}

export async function reportError(report: ErrorReport): Promise<void> {
  if (silentPatterns.some(p => report.message?.includes(p))) return;

  if (suppressClientMonitoringEvent(report.message || '', report.stack)) return;

  const fingerprint = getFingerprint(report.message, report.url);
  if (isDuplicate(fingerprint)) return;

  const user = getUserFromToken();

  const fullReport: ErrorReport = {
    ...report,
    url: report.url || window.location.pathname,
    userAgent: navigator.userAgent,
    severity: report.severity || 'error',
    userId: report.userId || user.userId,
    userEmail: report.userEmail || user.userEmail,
  };

  try {
    await fetch(`${API_URL}/client-errors/report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fullReport),
      keepalive: true,
    });
  } catch {
    // silently fail — don't cause errors from error reporting
  }
}

export function setupGlobalErrorHandlers(): void {
  window.addEventListener('error', (event) => {
    if (silentPatterns.some(p => event.message?.includes(p))) return;

    const message = event.message || 'Unknown error';
    if (suppressClientMonitoringEvent(message, event.error?.stack)) return;

    reportError({
      message,
      stack: event.error?.stack,
      url: window.location.pathname,
      metadata: { lineno: event.lineno, colno: event.colno, filename: event.filename },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    const message = error instanceof Error
      ? error.message
      : String(error || 'Unhandled Promise Rejection');

    if (silentPatterns.some(p => message.includes(p))) return;
    if (suppressClientMonitoringEvent(message, error instanceof Error ? error.stack : undefined)) return;

    reportError({
      message,
      stack: error instanceof Error ? error.stack : undefined,
      metadata: { type: 'unhandledrejection' },
    });
  });
}
